// feature/carpetas-phase2-docs — P2. Proceso-aware document panel.
// Renders the slot list for a case in chronological pipeline order
// (tutela_escrito → auto_admisorio → actuacion_juzgado [non-tutela
// only] → contestacion → email_print_juzgado), pulls each slot's
// most-recent doc from any case in the same proceso, and lets
// admin/carpetas/directora upload missing slots inline.
//
// Reuses existing styling (glass-section + badges + warn-inline)
// rather than introducing a primitive.
import React, { useEffect, useRef, useState } from "react";
import { carpetas } from "../api/endpoints";
import { apiBase } from "../api/client";
import { useToast } from "./Toast";
import Spinner from "./Spinner";

const SLOT_LABELS = {
  tutela_escrito:      "Tutela escrito",
  auto_admisorio:      "Auto admisorio",
  actuacion_juzgado:   "Actuación del juzgado",
  contestacion:        "Contestación",
  email_print_juzgado: "Impresión del correo",
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("es-CO") : "—";

export default function CarpetaDocumentsPanel({ caseId, canUpload }) {
  const [data, setData] = useState(null);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [warningBySlot, setWarningBySlot] = useState({});
  const fileInputs = useRef({});
  const toast = useToast();

  function load() {
    if (!caseId) return;
    carpetas.getCarpetaDocuments(caseId)
      .then(r => setData(r.data))
      .catch(() => setData({ documents: [], completeness: { required: 0, present: 0, missing_slots: [] } }));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  async function handleUpload(slotType, file, existingDocId) {
    if (!file) return;
    setUploadingSlot(slotType);
    setWarningBySlot(w => ({ ...w, [slotType]: null }));
    try {
      // P2.2 change 3 — when there's already a doc in the slot, the
      // button labelled "Reemplazar" hits the replace endpoint
      // (soft-delete prior + insert new). When there's nothing there,
      // hit the upload endpoint as before.
      const r = existingDocId
        ? await carpetas.replaceCarpetaDoc(caseId, existingDocId, file, {
            as_carpeta: slotType === "contestacion" ? true : false,
          })
        : await carpetas.uploadCarpetaDoc(caseId, slotType, file, {
            // contestacion is shared between Path A (abogado-own-case) and
            // Path B (carpeta upload). Carpeta panel always wants Path B.
            as_carpeta: slotType === "contestacion" ? true : false,
          });
      if (r.data?.pdf_title_warning) {
        setWarningBySlot(w => ({ ...w, [slotType]: r.data.pdf_title_warning_message || "El título del PDF no coincide con el formato esperado." }));
        toast.push("Documento cargado con advertencia. Revise el documento.", "warning");
      } else {
        toast.push(`${SLOT_LABELS[slotType] || slotType} ${existingDocId ? "reemplazado" : "cargado"}.`, "success");
      }
      load();
    } catch (e) {
      const data = e?.response?.data;
      toast.push(data?.message || data?.error || "Error al cargar", "error");
    } finally {
      setUploadingSlot(null);
      // Reset the file input so re-uploading the same filename works.
      if (fileInputs.current[slotType]) fileInputs.current[slotType].value = "";
    }
  }

  if (!data) {
    return (
      <div className="glass-section">
        <h3 style={{ marginTop: 0 }}>Documentos de la carpeta</h3>
        <Spinner size={16} />
      </div>
    );
  }

  const { completeness, documents } = data;
  const isComplete = completeness.present === completeness.required && completeness.required > 0;
  const badge = (
    <span className={"badge " + (isComplete ? "mint" : "muted")} style={{ fontSize: 11 }}>
      {completeness.present}/{completeness.required}{isComplete ? " ✓" : ""}
    </span>
  );

  return (
    <div className="glass-section">
      <div className="row" style={{ alignItems: "center", marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Documentos de la carpeta</h3>
        <span className="muted" style={{ fontSize: 11, marginLeft: 8 }}>
          {data.case_tipo === "tutela" ? "Tutela · 4 documentos" : "Actuación · 5 documentos"}
        </span>
        <div style={{ flex: 1 }} />
        {badge}
      </div>

      {/* P2.2 change 5 removed the P2.1 hint — non-uploaders now see
          the CarpetaStatusBadge instead of this panel entirely, so
          the "carpetas team will upload" copy moved out of view. */}

      <table style={{ width: "100%" }}>
        <tbody>
          {documents.map(slot => {
            const lbl = SLOT_LABELS[slot.slot] || slot.slot;
            const doc = slot.uploaded_doc;
            const warn = warningBySlot[slot.slot];
            return (
              <tr key={slot.slot} style={{ borderBottom: "1px solid var(--border-hairline)" }}>
                <td style={{ padding: "8px 4px", width: "30%", verticalAlign: "top" }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{lbl}</div>
                </td>
                <td style={{ padding: "8px 4px", verticalAlign: "top" }}>
                  {doc ? (
                    <div>
                      <a
                        href={`${apiBase || ""}${doc.url}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 13, fontFamily: "monospace" }}
                      >
                        {doc.canonical_filename}
                      </a>
                      <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                        Subido por {doc.uploaded_by_name || "—"} el {fmtDate(doc.uploaded_at)}
                        {doc.case_number_origin && doc.case_number_origin !== undefined && (
                          <> · origen: {doc.case_number_origin}</>
                        )}
                      </div>
                      {warn && (
                        <div className="warn-inline" style={{
                          marginTop: 6, padding: 6, fontSize: 11,
                          background: "var(--color-warning-bg)",
                          borderColor: "var(--color-warning)",
                          color: "var(--color-warning)",
                        }}>{warn}</div>
                      )}
                    </div>
                  ) : (
                    <span className="muted" style={{ fontSize: 12, fontStyle: "italic" }}>
                      Falta — sin cargar
                    </span>
                  )}
                </td>
                <td style={{ padding: "8px 4px", textAlign: "right", verticalAlign: "top" }}>
                  {canUpload && (
                    <>
                      <input
                        type="file"
                        accept="application/pdf"
                        ref={el => { fileInputs.current[slot.slot] = el; }}
                        style={{ display: "none" }}
                        onChange={e => {
                          const f = e.target.files?.[0];
                          if (f) handleUpload(slot.slot, f, doc?.id || null);
                        }}
                      />
                      <button
                        className="ghost sm"
                        disabled={uploadingSlot === slot.slot}
                        onClick={() => fileInputs.current[slot.slot]?.click()}
                      >
                        {uploadingSlot === slot.slot
                          ? <><Spinner size={12} /> Subiendo…</>
                          : (doc ? "Reemplazar" : "Subir")}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
